'use client'

import { useMemo } from 'react'
import { usePresenceSnapshot } from '@/hooks/presence/usePresenceSnapshot'
import { useSystemUsers } from '@/hooks/useSystemUsers'
import { presenceOf } from '@/lib/presence/presenceTime'
import type ISystemUser from '@/types/SystemUser'
import type { PresenceStatus } from '@/types/presence'

export interface PresenceCounts {
  online: number
  afk: number
  offline: number
}

/** Identidade (directory) + presença (snapshot), casadas por `db_id`. */
export interface RosterEntry extends ISystemUser {
  /** `null` = sem grant de presença OU sem entrada no snapshot. NÃO é 'offline':
   *  o fallback pra offline é de APRESENTAÇÃO (`presenceOf`), e misturar os dois
   *  faria a tela afirmar "offline" sobre quem ela não consegue ver. */
  presence_status: PresenceStatus | null
  last_seen_at: number | null
}

/**
 * Roster do tenant + contadores. **Duas fontes, dois grants**: identidade vem de
 * `users:directory:read` e presença de `users:presence:read`. Sem o segundo o
 * roster continua inteiro, só sem estado — é o que mantém nome e avatar de pé
 * num tenant que não concede presença.
 */
export function usePresenceRoster() {
  const { systemUsers, isLoading: identidadeCarregando, isError: identidadeErro } = useSystemUsers()
  const { byId, isLoading: presencaCarregando, isError: presencaErro } = usePresenceSnapshot()

  const roster = useMemo<RosterEntry[]>(
    () =>
      systemUsers.map((u) => {
        const p = byId.get(u.db_id)
        return { ...u, presence_status: p?.presence_status ?? null, last_seen_at: p?.last_seen_at ?? null }
      }),
    [systemUsers, byId],
  )

  const counts = useMemo(
    () =>
      roster.reduce<PresenceCounts>(
        (acc, u) => {
          acc[presenceOf(u)] += 1
          return acc
        },
        { online: 0, afk: 0, offline: 0 },
      ),
    [roster],
  )

  return {
    roster,
    counts,
    isLoading: identidadeCarregando || presencaCarregando,
    isError: identidadeErro || presencaErro,
  }
}
